import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { ethers } from 'ethers';
import { handleGetChainInfo } from '@/utils/wallet';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from '@/providers/FeatureFlagProvider';
import { getL2ChainInfo, getCryptoAssets } from '@/resources/wallet-service.resource';
import { getBlockchainConfigurations } from '@/resources/nft-service.resource';
import { ERC20_ABI } from './swap/abi';
import { getSwapProvider } from '@/providers/swap/core/getSwapProvider';
import {
  BitcoinCryptoAssetProvider,
  CryptoAssetProviderResolver,
  EvmBaseErc20CryptoAssetProvider,
  EvmBaseNativeCryptoAssetProvider,
  EvmErc20CryptoAssetProvider,
  EvmLayer2NativeCryptoAssetProvider,
  EvmNativeCryptoAssetProvider,
  createCryptoAssets,
} from '@/providers/cryptoAssets';
import { createWalletServiceClient } from '@/resources/wallet-service.client';

import {
  getEncryptedData,
  decryptMnemonic,
  decryptLegacyMnemonic,
  prepareWallet,
} from '@/providers/helpers/web3Helper';

import { config } from '../config';

const Web3Context = createContext(null);

// get from backend?
const NETWORKS = {
  1: {
    name: 'Mainnet',
  },
  11155111: {
    name: 'Sepolia',
  },
};

const normalizeCryptoAssetsResponse = (response) => {
  if (Array.isArray(response)) return createCryptoAssets(response);
  if (Array.isArray(response?.cryptoAssets)) {
    return createCryptoAssets(response.cryptoAssets);
  }
  return createCryptoAssets([]);
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

export function Web3Provider({ children }) {
  const { fetcher, status, authStatuses } = useAuth();
  const { featureFlags, isLoading: featureFlagsLoading } = useFeatureFlags();
  const [blockChainConfig, setBlockChainConfig] = useState([]);
  const chainIdIntervalRef = useRef(null);

  const getBlockchainConfig = async () => {
    const { blockchainConfigurations } = await fetcher(getBlockchainConfigurations());
    setBlockChainConfig(blockchainConfigurations);
  };

  const [error, setError] = useState(null);
  const [chainInfo, setChainInfo] = useState(null);
  const [l2ChainInfo, setL2ChainInfo] = useState(null);
  const [networks, setNetworks] = useState(NETWORKS);
  const [cryptoAssets, setCryptoAssets] = useState([]);

  useEffect(() => {
    if (authStatuses.SIGNED_IN !== status) {
      return;
    }
    getBlockchainConfig();
    const fetchAndSetChainId = async () => {
      if (status !== authStatuses.SIGNED_IN) {
        if (chainIdIntervalRef.current) clearInterval(chainIdIntervalRef.current);
        return;
      }

      getL2ChainData();

      const chainInfo = await handleGetChainInfo(fetcher);

      if (chainInfo?.chainId) {
        setChainInfo(chainInfo);
        try {
          const cryptoAssetsResponse = await fetcher(getCryptoAssets());
          setCryptoAssets(normalizeCryptoAssetsResponse(cryptoAssetsResponse));
        } catch (err) {
          console.error('Failed to fetch Crypto Assets:', err);
        }
      }
    };

    if (chainInfo?.chainId) {
      // Immediately fetch CryptoAsset configs to update the placeholders
      fetcher(getCryptoAssets())
        .then((data) => {
          setCryptoAssets(normalizeCryptoAssetsResponse(data));
        })
        .catch((err) => {
          console.error('Failed to fetch Crypto Assets:', err);
        });

      return;
    } else {
      fetchAndSetChainId();
    }
  }, [status, authStatuses.SIGNED_IN, fetcher]);

  useEffect(() => {
    if (blockChainConfig.length) {
      // If not sepolia or mainnet, update network data from db
      blockChainConfig.forEach((blockchain) => {
        if (blockchain.chainId !== 1 && blockchain.chainId !== 11155111) {
          setNetworks((prevNetworks) => ({
            ...prevNetworks,
            [blockchain.chainId]: {
              name: blockchain.name,
              rpcUrl: blockchain.rpcUrl,
            },
          }));
        }
      });
    }
  }, [blockChainConfig]);

  const provider = useMemo(() => {
    if (!chainInfo) return null;
    try {
      const chainId = chainInfo?.chainId;
      const network = networks[chainId];

      if (!network?.name) {
        throw new Error(`Unsupported chain ID: ${chainId}`);
      }
      // using nextjs server side api call to protect key
      const url = `${window.location.origin}/api/infura`;
      const provider = new ethers.providers.JsonRpcProvider(url, Number(chainId));

      return provider;
    } catch (err) {
      setError('Failed to initialize provider');
      console.error('Provider initialization error:', err);
      return null;
    }
  }, [chainInfo?.chainId, networks]);

  const useElevateSwapProvider =
    process.env.NEXT_PUBLIC_USE_ELEVATE_SWAP_PROVIDER === 'true' ||
    featureFlags?.useElevateSwapProvider === true;

  const swapProvider = useMemo(() => {
    if (featureFlagsLoading) {
      return null;
    }

    return getSwapProvider({
      chainId: Number(chainInfo?.chainId || 1),
      elevateSwapApiUrl: config.elevate.swapApiUrl,
      jsonRpcUrl: '/api/infura',
      useElevateSwapProvider,
    });
  }, [chainInfo?.chainId, featureFlagsLoading, useElevateSwapProvider]);

  const walletServiceClient = useMemo(
    () => createWalletServiceClient(fetcher),
    [fetcher]
  );

  const cryptoAssetProviderResolver = useMemo(() => {
    return new CryptoAssetProviderResolver([
      new BitcoinCryptoAssetProvider(walletServiceClient),
      new EvmBaseNativeCryptoAssetProvider(),
      new EvmBaseErc20CryptoAssetProvider(),
      new EvmLayer2NativeCryptoAssetProvider(walletServiceClient),
      new EvmNativeCryptoAssetProvider(),
      new EvmErc20CryptoAssetProvider(),
    ]);
  }, [walletServiceClient]);

  const isAllowedBuyToken = useCallback(
    (token) => {
      return swapProvider?.isAllowedBuyToken(token?.cryptoAsset) || false;
    },
    [swapProvider]
  );

  const getL2ChainData = async () =>
    await fetcher(getL2ChainInfo())
      .then((data) => {
        setL2ChainInfo(data);
      })
      .catch(() => console.error('Error fetching L2 chain data'));

  async function getBalance(address) {
    try {
      if (!provider) throw new Error('Provider not initialized');
      const balance = await provider.getBalance(address);
      return ethers.utils.formatEther(balance);
    } catch (err) {
      setError('Failed to get balance');
      console.error('Balance fetch error:', err);
      throw err;
    }
  }

  async function getTokenBalance(tokenAddress, walletAddress) {
    try {
      if (!provider) throw new Error('Provider not initialized');
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      const balance = await tokenContract.balanceOf(walletAddress);
      const decimals = await tokenContract.decimals();

      return ethers.utils.formatUnits(balance, decimals);
    } catch (err) {
      setError('Failed to get token balance');
      console.error('Token balance fetch error:', err);
      throw err;
    }
  }

  async function getLatestBlock() {
    try {
      if (!provider) throw new Error('Provider not initialized');
      return await provider.getBlock('latest');
    } catch (err) {
      setError('Failed to get latest block');
      console.error('Block fetch error:', err);
      throw err;
    }
  }

  async function bridgePublicToPrivate({
    passcode,
    amountBN,
    sourceProvider,
    destinationProvider,
    sourceAssetLockContractAddress,
    tokenAddress,
    releaseFee,
    destinationAssetReleaseContractAddress,
    decimals,
  }) {
    try {
      const sourceWallet = await prepareWallet(fetcher, passcode, sourceProvider);
      const destinationWallet = await prepareWallet(
        fetcher,
        passcode,
        destinationProvider
      );
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function approve(address spender, uint256 amount) external returns (bool)'],
        sourceWallet
      );
      const publicBridgeContract = new ethers.Contract(
        sourceAssetLockContractAddress,
        ['function lockTokens(uint256 amount, uint8 sourceChain) external'],
        sourceWallet
      );
      const privateBridgeContract = new ethers.Contract(
        destinationAssetReleaseContractAddress,
        ['function payReleaseFee() external payable'],
        destinationWallet
      );

      const releaseFeeBN = ethers.utils.parseUnits(releaseFee.toString(), decimals);

      await privateBridgeContract.payReleaseFee({ value: releaseFeeBN });

      const approveTx = await tokenContract.approve(
        sourceAssetLockContractAddress,
        amountBN
      );
      await approveTx.wait();

      const lockTx = await publicBridgeContract.lockTokens(amountBN, 0, {
        gasLimit: 500000n,
      });

      const receipt = await lockTx.wait();
      return receipt;
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function bridgePrivateToPublic({
    passcode,
    amountBN,
    sourceProvider,
    destinationProvider,
    sourceAssetLockContractAddress,
    releaseFee,
    destinationAssetReleaseContractAddress,
  }) {
    try {
      const sourceWallet = await prepareWallet(fetcher, passcode, sourceProvider);
      const destinationWallet = await prepareWallet(
        fetcher,
        passcode,
        destinationProvider
      );

      const publicContract = new ethers.Contract(
        destinationAssetReleaseContractAddress,
        ['function payReleaseFee() external payable'],
        destinationWallet
      );

      // Let's try awaiting this and see if it takes to long
      await publicContract.payReleaseFee({ value: releaseFee, gasLimit: 300000n });

      const privateBridgeContract = new ethers.Contract(
        sourceAssetLockContractAddress,
        ['function lockTokens(uint8) external payable'],
        sourceWallet
      );

      const enumForChain = 1; // 1 for eth, 2 for binance This needs to be from somewhere, it is not the chain id will be added to bridgexconfig table
      const tx = await privateBridgeContract.lockTokens(enumForChain, {
        value: amountBN,
        gasLimit: 500000n,
      });

      const receipt = await tx.wait();

      return receipt;
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function signTransaction(passcode, to, value) {
    try {
      const encryptedData = await getEncryptedData(fetcher);

      if (!encryptedData) throw new Error('No encrypted data available');

      let mnemonic = null;
      let wallet = null;
      let signedTx = null;

      try {
        mnemonic = decryptMnemonic(encryptedData, passcode);
        wallet = ethers.Wallet.fromMnemonic(mnemonic);
        const connectedWallet = wallet.connect(provider);
        const latestNonce = await provider.getTransactionCount(wallet.address, 'latest');

        const tx = {
          to,
          value: ethers.utils.parseEther(value.toString()),
          nonce: latestNonce,
          gasLimit: ethers.utils.hexlify(100000),
          gasPrice: await provider.getGasPrice(),
        };

        signedTx = await connectedWallet.signTransaction(tx);

        return signedTx;
        // if you are ready to send transaction on signing instead of return signedTx we can use const txResponse = await provider.sendTransaction(signedTx);
      } finally {
        // Clear sensitive data from memory
        if (mnemonic) mnemonic = null;
        wallet = null;
      }
    } catch (err) {
      console.error('Transaction signing error:', err);
      throw err;
    }
  }

  async function sendWeb3Transaction({
    passcode,
    from,
    to,
    amount,
    cryptoAsset,
    isMaxSend,
    core = true,
  }) {
    if (!cryptoAsset) {
      throw new Error('CryptoAsset is required to send a transaction');
    }

    const assetProvider = cryptoAssetProviderResolver.requireProvider(cryptoAsset);

    if (!core) {
      const privateKey = await decryptLegacyMnemonic(from, passcode, fetcher); // Returns private key for legacy wallets

      return assetProvider.send({
        amount,
        cryptoAsset,
        from,
        isMaxSend,
        privateKey,
        to,
      });
    } else {
      const encryptedData = await getEncryptedData(fetcher);
      let mnemonic = '';

      mnemonic = decryptMnemonic(encryptedData, passcode);

      try {
        return await assetProvider.send({
          amount,
          cryptoAsset,
          from,
          isMaxSend,
          mnemonic,
          to,
        });
      } finally {
        mnemonic = '';
      }
    }
  }

  const getSwapQuote = useCallback(
    async ({ fromCryptoAsset, toCryptoAsset, recipient, signer, amount }) => {
      try {
        if (!swapProvider) throw new Error('Swap provider not initialized');

        const quote = await swapProvider.getSwapQuote({
          fromCryptoAsset,
          toCryptoAsset,
          amount,
          recipient,
          signer,
        });

        if (!quote) throw new Error('Failed to get quote');

        console.info('Returning quote:', quote);
        return quote;
      } catch (err) {
        console.error('Failed to get swap quote:', err);
        throw err;
      }
    },
    [swapProvider]
  );

  const getMaxSwapAmount = useCallback(
    async ({
      fromCryptoAsset,
      toCryptoAsset,
      recipient,
      signer,
      balance,
      precision,
      priceUsd,
    }) => {
      if (!swapProvider) throw new Error('Swap provider not initialized');

      return swapProvider.getMaxSwapAmount({
        fromCryptoAsset,
        toCryptoAsset,
        amount: balance,
        recipient,
        signer,
        balance,
        precision,
        priceUsd,
      });
    },
    [swapProvider]
  );

  async function executeSwap({
    fromCryptoAsset,
    toCryptoAsset,
    amount,
    slippage = 0.3,
    wallet,
  }) {
    if (!wallet) throw new Error('Wallet not provided');
    if (!swapProvider) throw new Error('Swap provider not initialized');

    return swapProvider.executeSwap({
      fromCryptoAsset,
      toCryptoAsset,
      amount,
      slippage,
      wallet,
    });
  }

  async function getWalletFromPasscode(passcode) {
    const encryptedData = await getEncryptedData(fetcher);
    if (!encryptedData) throw new Error('No encrypted data available');

    try {
      const mnemonic = decryptMnemonic(encryptedData, passcode);

      const wallet = ethers.Wallet.fromMnemonic(mnemonic);

      return wallet;
    } catch (err) {
      throw new Error('Invalid passcode: ', +err.message);
    }
  }

  const value = {
    chainInfo,
    cryptoAssets,
    cryptoAssetProviderResolver,
    error,
    executeSwap,
    getBalance,
    getMaxSwapAmount,
    getSwapQuote,
    getTokenBalance,
    getLatestBlock,
    getWalletFromPasscode,
    isAllowedBuyToken,
    l2ChainInfo,
    provider,
    sendWeb3Transaction,
    bridgePrivateToPublic,
    bridgePublicToPrivate,
    signTransaction,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}
